// (c) chaehongee. Confidential and proprietary.

import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'
import type { Corner, Rect } from '../../types'
import { useAppSelector } from '../../../../store/hooks/hooks'
import { setPosition, setPositions, type CanvasPosition } from '../../../../store/slices/canvasPositionsSlice'

/**
 * Canvas Items Mental Model
 * 
 * - Image (worldRects): Full image with transform (position, size via TransformOverlay)
 * - Initial (initialWorldRects): Starting point for revert (captured on first placement)
 * 
 * Note: Frame editing state is now managed separately in useFrameEditState
 */

const GAP = 64
const MIN_SIZE = 10
const PLACEHOLDER_SIZE = 512

interface UseWorldItemsArgs {
    containerRef: React.RefObject<HTMLDivElement | null>
    clientToWorld: (clientX: number, clientY: number) => { x: number; y: number } | null
    imageRectInContainer: Rect | null
    imageSizesBySrc: Record<string, { w: number; h: number }>
    multiSelectionNodes: Array<{ id: string; src: string; mediaType?: string }>
    disableAutoRowReflow?: boolean
    suppressInitialPlacement?: boolean
    onImageRectChange?: (id: string, rect: Rect) => void
    sessionId?: string | null
    dispatch?: (action: any) => void
    onImageResizeCommit?: (info: { id: string; rect: Rect; src?: string }) => void
    /** Global selection IDs for group drag */
    selectedNodeIds?: string[]
    /** Current tool mode — 'grab' disables item interactions */
    toolMode?: 'pointer' | 'grab' | 'select'
}


interface LayerHandlers {
    onImageSelect: (id: string) => void
    onImageMoveStart: (id: string, e: React.PointerEvent, src?: string) => void
    onImageResizeStart: (id: string, src: string, corner: Corner, e: React.PointerEvent) => void
}

export interface UseWorldItemsResult {
    worldRectsById: Record<string, Rect>
    initialWorldRectsById: Record<string, Rect>
    setWorldRects: (next: Record<string, Rect>) => void
    setInitialWorldRects: (next: Record<string, Rect>) => void
    worldRectsList: Array<{ id: string; src: string; mediaType?: string; rect: Rect }>
    selectedWorldItemId: string | null
    clearLocalSelection: () => void
    layerHandlers: LayerHandlers
    onPointerMove: (e: React.PointerEvent<HTMLDivElement>) => boolean
    onPointerUp: (e: React.PointerEvent<HTMLDivElement>) => boolean
}

export function useItemTransforms(args: UseWorldItemsArgs): UseWorldItemsResult {
    const { containerRef, clientToWorld, imageSizesBySrc, multiSelectionNodes, disableAutoRowReflow, suppressInitialPlacement, onImageRectChange, sessionId, dispatch, onImageResizeCommit, selectedNodeIds, toolMode } = args

    const [worldRectsById, setWorldRectsById] = useState<Record<string, Rect>>({})
    const [initialWorldRectsById, setInitialWorldRectsById] = useState<Record<string, Rect>>({})
    const [selectedWorldItemId, setSelectedWorldItemId] = useState<string | null>(null)

    // Persistent source-of-truth for item rects (world-space) per session.
    // We keep a local copy for smooth pointer-drag updates, but we hydrate/sync
    // from Redux and persist bulk updates (layout/undo/redo/etc) back to Redux.
    const persistedPositions = useAppSelector((state) =>
        sessionId ? state.canvasPositions.positions[sessionId] : null
    )

    const srcById = useMemo(() => {
        const map: Record<string, string> = {}
        for (const item of multiSelectionNodes) {
            map[item.id] = item.src
        }
        return map
    }, [multiSelectionNodes])

    const resizeRef = useRef<{
        id: string
        corner: Corner
        start: { x: number; y: number }
        startRect: Rect
        constrainAspect: boolean
        resizeFromCenter: boolean
    } | null>(null)
    const moveRef = useRef<{
        id: string
        start: { x: number; y: number }
        startRect: Rect
        /** When group-dragging, start rects for all selected items */
        groupStartRects?: Record<string, Rect>
    } | null>(null)
    const userInteractedRef = useRef<boolean>(false)

    // Hydrate/sync local world rects from persisted positions (Redux).
    // Avoid overwriting while the user is actively dragging/resizing.
    useLayoutEffect(() => {
        if (!sessionId) return
        if (!persistedPositions || Object.keys(persistedPositions).length === 0) return
        if (moveRef.current || resizeRef.current) return

        const nextRects: Record<string, Rect> = {}
        for (const [id, pos] of Object.entries(persistedPositions)) {
            nextRects[id] = { x: pos.x, y: pos.y, w: pos.w, h: pos.h }
        }

        setWorldRectsById((prev) => {
            if (shallowRectsEqual(prev, nextRects)) return prev
            return nextRects
        })
    }, [sessionId, persistedPositions])

    // Initialize world rects for new selections without overriding existing positions
    useLayoutEffect(() => {
        if (multiSelectionNodes.length === 0) return
        if (suppressInitialPlacement) return
        setWorldRectsById((prev) => {
            let changed = false
            const next = { ...prev }
            let cursorX = 0
            for (const item of multiSelectionNodes) {
                const { id, src } = item
                if (!prev[id]) {
                    const sz = imageSizesBySrc[src] || { w: PLACEHOLDER_SIZE, h: PLACEHOLDER_SIZE }
                    next[id] = { x: cursorX, y: 0, w: sz.w, h: sz.h }
                    cursorX += sz.w + GAP
                    changed = true
                } else {
                    cursorX = prev[id].x + prev[id].w + GAP
                }
            }
            return changed ? next : prev
        })
    }, [multiSelectionNodes, imageSizesBySrc])

    // After images load, replace placeholder 512x512 rects with true natural sizes
    useLayoutEffect(() => {
        if (multiSelectionNodes.length === 0) return
        setWorldRectsById((prev) => {
            let changed = false
            const next = { ...prev }
            const changedIds: string[] = []
            for (const { id, src } of multiSelectionNodes) {
                const sz = imageSizesBySrc[src]
                if (!sz) continue
                const curr = next[id]
                if (!curr) continue
                const isPlaceholder = Math.abs(curr.w - PLACEHOLDER_SIZE) < 0.5 && Math.abs(curr.h - PLACEHOLDER_SIZE) < 0.5
                if (isPlaceholder && (curr.w !== sz.w || curr.h !== sz.h)) {
                    next[id] = { x: curr.x, y: curr.y, w: sz.w, h: sz.h }
                    changed = true
                    changedIds.push(id)
                }
            }
            if (changed) {
                // If user hasn't interacted yet, reflow to avoid overlaps after widths expand
                if (!userInteractedRef.current && !disableAutoRowReflow) {
                    const orderedIds = multiSelectionNodes.map(({ id }) => id)
                    // Start reflow from the first item whose size actually changed.
                    const firstChangedIndex = Math.max(0, orderedIds.findIndex((id) => changedIds.includes(id)))
                    const firstId = firstChangedIndex >= 0 ? orderedIds[firstChangedIndex] : orderedIds[0]
                    const startX = firstId && next[firstId] ? next[firstId].x : 0
                    let cursorX = startX
                    // Keep items before firstChangedIndex anchored; reflow from firstChangedIndex forward
                    for (let i = firstChangedIndex; i < orderedIds.length; i++) {
                        const id = orderedIds[i]
                        const rect = next[id]
                        if (!rect) continue
                        if (i === firstChangedIndex) {
                            cursorX = rect.x + rect.w + GAP
                            continue
                        }
                        next[id] = { ...rect, x: cursorX }
                        cursorX += rect.w + GAP
                    }
                }
            }
            return changed ? next : prev
        })
        // Frames do not auto-adjust with image size changes
        // They are set once when enabled and remain static until explicitly modified
    }, [multiSelectionNodes, imageSizesBySrc, worldRectsById])

    // Capture initial world rect when we first have both a worldRect and natural size
    // IMPORTANT: Only capture after placeholder has been replaced with actual dimensions
    useLayoutEffect(() => {
        if (multiSelectionNodes.length === 0) return
        setInitialWorldRectsById((prev) => {
            let changed = false
            const next = { ...prev }
            for (const { id, src } of multiSelectionNodes) {
                // Only set initial if we don't have one yet and we have both world rect and natural size
                if (!prev[id]) {
                    const worldRect = worldRectsById[id]
                    const naturalSize = imageSizesBySrc[src]
                    if (worldRect && naturalSize) {
                        // CRITICAL: Skip if this is still a placeholder
                        // We must wait for the placeholder to be replaced with actual dimensions
                        const isPlaceholder = Math.abs(worldRect.w - PLACEHOLDER_SIZE) < 0.5 && Math.abs(worldRect.h - PLACEHOLDER_SIZE) < 0.5
                        if (isPlaceholder) {
                            continue
                        }

                        // Record the first "committed" world rect as the baseline
                        next[id] = { ...worldRect }
                        changed = true
                    }
                }
            }
            return changed ? next : prev
        })
    }, [multiSelectionNodes, worldRectsById, imageSizesBySrc])

    // Cleanup initialWorldRects when items are removed from canvas
    useLayoutEffect(() => {
        const currentIds = new Set(multiSelectionNodes.map(({ id }) => id))
        setInitialWorldRectsById((prev) => {
            const next = { ...prev }
            let changed = false
            for (const id of Object.keys(prev)) {
                if (!currentIds.has(id)) {
                    delete next[id]
                    changed = true
                }
            }
            return changed ? next : prev
        })
    }, [multiSelectionNodes])

    const worldRectsList = useMemo(() => {
        let cursorX = 0
        return multiSelectionNodes.map(({ id, src, mediaType }) => {
            const existing = worldRectsById[id]
            if (existing) {
                cursorX = existing.x + existing.w + GAP
                return { id, src, mediaType, rect: existing }
            }
            const sz = imageSizesBySrc[src] || { w: PLACEHOLDER_SIZE, h: PLACEHOLDER_SIZE }
            const x = suppressInitialPlacement ? 0 : cursorX
            const fallback = { x, y: 0, w: sz.w, h: sz.h }
            if (!suppressInitialPlacement) {
                cursorX += sz.w + GAP
            }
            return { id, src, mediaType, rect: fallback }
        })
    }, [multiSelectionNodes, worldRectsById, imageSizesBySrc, suppressInitialPlacement])

    const startResize = useCallback((id: string, src: string, corner: Corner, e: React.PointerEvent) => {
        e.stopPropagation()
        userInteractedRef.current = true
        const w = clientToWorld(e.clientX, e.clientY)
        if (!w) return
        let base = worldRectsById[id]
        if (!base) {
            const sz = imageSizesBySrc[src]
            base = (sz ? { x: 0, y: 0, w: sz.w, h: sz.h } : { x: 0, y: 0, w: PLACEHOLDER_SIZE, h: PLACEHOLDER_SIZE })
            setWorldRectsById((prev) => ({ ...prev, [id]: base! }))
        }
        resizeRef.current = {
            id,
            corner,
            start: w,
            startRect: { ...base },
            constrainAspect: !!(e as any).shiftKey,
            resizeFromCenter: !!(e as any).altKey,
        }
        const el = containerRef.current
        if (el) { try { el.setPointerCapture(e.pointerId) } catch { } }
    }, [clientToWorld, worldRectsById, imageSizesBySrc, containerRef])

    const startMove = useCallback((id: string, e: React.PointerEvent, src?: string) => {
        // Grab mode: don't capture, let event bubble for panning
        if (toolMode === 'grab') return
        e.stopPropagation()
        userInteractedRef.current = true
        const w = clientToWorld(e.clientX, e.clientY)
        if (!w) return
        let base = worldRectsById[id]
        if (!base) {
            const sz = src ? imageSizesBySrc[src] : undefined
            base = (sz ? { x: 0, y: 0, w: sz.w, h: sz.h } : { x: 0, y: 0, w: PLACEHOLDER_SIZE, h: PLACEHOLDER_SIZE })
            setWorldRectsById((prev) => ({ ...prev, [id]: base! }))
        }
        // Group drag: if dragged item is in selection and multiple selected
        const isInSelection = selectedNodeIds?.includes(id) ?? false
        const isGroupDrag = isInSelection && (selectedNodeIds?.length ?? 0) > 1
        let groupStartRects: Record<string, Rect> | undefined
        if (isGroupDrag && selectedNodeIds) {
            groupStartRects = {}
            for (const sid of selectedNodeIds) {
                const r = worldRectsById[sid]
                if (r) groupStartRects[sid] = { ...r }
            }
        }
        moveRef.current = { id, start: w, startRect: { ...base }, groupStartRects }
        const el = containerRef.current
        if (el) { try { el.setPointerCapture(e.pointerId) } catch { } }
    }, [clientToWorld, worldRectsById, imageSizesBySrc, containerRef, selectedNodeIds, toolMode])


    const onPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
        // Image resize
        if (resizeRef.current) {
            const info = resizeRef.current
            const w = clientToWorld(e.clientX, e.clientY)
            if (!info || !w) return true
            const dx = w.x - info.start.x
            const dy = w.y - info.start.y
            const r0 = info.startRect
            let nx = r0.x
            let ny = r0.y
            let nw = r0.w
            let nh = r0.h
            if (info.corner === 'nw') { nx = r0.x + dx; ny = r0.y + dy; nw = r0.w - dx; nh = r0.h - dy }
            else if (info.corner === 'ne') { ny = r0.y + dy; nw = r0.w + dx; nh = r0.h - dy }
            else if (info.corner === 'sw') { nx = r0.x + dx; nw = r0.w - dx; nh = r0.h + dy }
            else if (info.corner === 'se') { nw = r0.w + dx; nh = r0.h + dy }
            else if (info.corner === 'n') { ny = r0.y + dy; nh = r0.h - dy }
            else if (info.corner === 's') { nh = r0.h + dy }
            else if (info.corner === 'w') { nx = r0.x + dx; nw = r0.w - dx }
            else if (info.corner === 'e') { nw = r0.w + dx }
            // Preserve aspect ratio when shift was held on drag start
            if (info.constrainAspect) {
                const ratio = (r0.w || 1) / (r0.h || 1)
                // Decide which dimension drives based on larger absolute change in screen space
                const driveWidth = Math.abs(nw - r0.w) >= Math.abs(nh - r0.h)
                if (driveWidth) {
                    nh = nw / (ratio || 1)
                    // Adjust y to keep opposite edge anchored for vertical change
                    if (info.corner === 'n' || info.corner === 'nw' || info.corner === 'ne') {
                        ny = r0.y + (r0.h - nh)
                    }
                } else {
                    nw = nh * ratio
                    // Adjust x to keep opposite edge anchored for horizontal change
                    if (info.corner === 'w' || info.corner === 'nw' || info.corner === 'sw') {
                        nx = r0.x + (r0.w - nw)
                    }
                }
            }
            // Resize from center if Alt was held on drag start
            if (info.resizeFromCenter) {
                const cx = r0.x + r0.w / 2
                const cy = r0.y + r0.h / 2
                nx = cx - nw / 2
                ny = cy - nh / 2
            }
            nw = Math.max(MIN_SIZE, nw)
            nh = Math.max(MIN_SIZE, nh)
            const newRect = { x: nx, y: ny, w: nw, h: nh }
            requestAnimationFrame(() => {
                setWorldRectsById((prev) => ({ ...prev, [info.id]: newRect }))
                onImageRectChange?.(info.id, newRect)
            })
            return true
        }
        // Move world item if dragging
        if (moveRef.current) {
            const info = moveRef.current
            const w = clientToWorld(e.clientX, e.clientY)
            if (!info || !w) return true
            const dx = w.x - info.start.x
            const dy = w.y - info.start.y

            if (info.groupStartRects) {
                // Group drag: move all selected items
                requestAnimationFrame(() => {
                    setWorldRectsById((prev) => {
                        const next = { ...prev }
                        for (const [sid, startRect] of Object.entries(info.groupStartRects!)) {
                            next[sid] = {
                                x: startRect.x + dx,
                                y: startRect.y + dy,
                                w: startRect.w,
                                h: startRect.h,
                            }
                        }
                        return next
                    })
                })
            } else {
                // Single item drag
                const r0 = info.startRect
                const newRect = { x: r0.x + dx, y: r0.y + dy, w: r0.w, h: r0.h }
                requestAnimationFrame(() => {
                    setWorldRectsById((prev) => ({ ...prev, [info.id]: newRect }))
                    onImageRectChange?.(info.id, newRect)
                })
            }
            return true
        }
        return false
    }, [clientToWorld, onImageRectChange])

    const onPointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
        const el = containerRef.current
        if (resizeRef.current) {
            const resizeInfo = resizeRef.current
            // Save manual position when resize completes
            if (sessionId && dispatch && resizeInfo.id) {
                const finalRect = worldRectsById[resizeInfo.id]
                if (finalRect) {
                    dispatch(setPosition({
                        sessionId,
                        nodeId: resizeInfo.id,
                        position: { ...finalRect, manuallySet: true },
                    }))
                    if (onImageResizeCommit) {
                        onImageResizeCommit({
                            id: resizeInfo.id,
                            rect: finalRect,
                            src: srcById[resizeInfo.id],
                        })
                    }
                }
            }
            resizeRef.current = null
            if (el) { try { el.releasePointerCapture(e.pointerId) } catch { } }
            return true
        }
        if (moveRef.current) {
            const moveInfo = moveRef.current
            // Save manual positions when move completes
            if (sessionId && dispatch) {
                if (moveInfo.groupStartRects) {
                    // Group drag: persist all moved items
                    for (const sid of Object.keys(moveInfo.groupStartRects)) {
                        const finalRect = worldRectsById[sid]
                        if (finalRect) {
                            dispatch(setPosition({
                                sessionId,
                                nodeId: sid,
                                position: { ...finalRect, manuallySet: true },
                            }))
                        }
                    }
                } else if (moveInfo.id) {
                    const finalRect = worldRectsById[moveInfo.id]
                    if (finalRect) {
                        dispatch(setPosition({
                            sessionId,
                            nodeId: moveInfo.id,
                            position: { ...finalRect, manuallySet: true },
                        }))
                    }
                }
            }
            moveRef.current = null
            if (el) { try { el.releasePointerCapture(e.pointerId) } catch { } }
            return true
        }
        return false
    }, [containerRef, sessionId, dispatch, worldRectsById, onImageResizeCommit, srcById])

    const layerHandlers: LayerHandlers = useMemo(() => ({
        onImageSelect: (id: string) => {
            if (toolMode === 'grab') return
            setSelectedWorldItemId(id)
        },
        onImageMoveStart: (id: string, e: React.PointerEvent, src?: string) => startMove(id, e, src),
        onImageResizeStart: (id: string, src: string, corner: Corner, e: React.PointerEvent) => startResize(id, src, corner, e),
    }), [startMove, startResize, toolMode])

    const clearLocalSelection = useCallback(() => {
        setSelectedWorldItemId(null)
    }, [])

    return {
        worldRectsById,
        initialWorldRectsById,
        setWorldRects: (next: Record<string, Rect>) => {
            setWorldRectsById(next)

            // Persist layout/undo/redo/etc to Redux as the canonical store.
            if (!sessionId || !dispatch) return

            const now = Date.now()
            // Persist only the current canvas items to prevent unbounded growth
            // (removed items should not linger in Redux/localStorage).
            const currentCanvasIds = new Set(multiSelectionNodes.map((n) => n.id))
            const merged: Record<string, CanvasPosition> = {}

            for (const id of currentCanvasIds) {
                const rect = next[id]
                const prev = persistedPositions?.[id]
                // If caller didn't provide a rect for a current canvas item, keep the previous
                // persisted value (avoids accidental erasure on partial updates).
                const chosen = rect ?? (prev ? { x: prev.x, y: prev.y, w: prev.w, h: prev.h } : null)
                if (!chosen) continue
                merged[id] = {
                    x: chosen.x,
                    y: chosen.y,
                    w: chosen.w,
                    h: chosen.h,
                    manuallySet: prev?.manuallySet ?? false,
                    // Preserve timestamp if present; otherwise stamp now.
                    timestamp: prev?.timestamp ?? now,
                }
            }

            dispatch(setPositions({ sessionId, positions: merged }))
        },
        setInitialWorldRects: (next: Record<string, Rect>) => { setInitialWorldRectsById(next) },
        worldRectsList,
        selectedWorldItemId,
        clearLocalSelection,
        layerHandlers,
        onPointerMove,
        onPointerUp,
    }
}

export default useItemTransforms

function shallowRectsEqual(a: Record<string, Rect>, b: Record<string, Rect>): boolean {
    const aKeys = Object.keys(a)
    const bKeys = Object.keys(b)
    if (aKeys.length !== bKeys.length) return false
    for (const k of aKeys) {
        const ar = a[k]
        const br = b[k]
        if (!br) return false
        if (
            ar.x !== br.x ||
            ar.y !== br.y ||
            ar.w !== br.w ||
            ar.h !== br.h
        ) {
            return false
        }
    }
    return true
}


